import { useEffect } from "react";

/** How many mounted screens have asked for a static height. A count rather
    than a flag so two overlapping screens can't have one's unmount release
    the other's freeze. `pins` is the stricter of the two — see the
    `freezeGrowth` option below. */
let holds = 0;
let pins = 0;

/** Whether `--app-height` may shrink. Read by useAppViewport on every
    measurement — module state rather than context because the hook that reads
    it is mounted at the very top of the tree (App) and the screens that set it
    are far below it, on the other side of AuthGate. */
export function isAppHeightFrozen(): boolean {
  return holds > 0 || pins > 0;
}

/** Whether `--app-height` may change at all. */
export function isAppHeightPinned(): boolean {
  return pins > 0;
}

export interface StaticAppHeightOptions {
  /** Hold the height against growth as well. Needed by a surface anchored to
      the *bottom* of #root, which only stays put if #root's bottom edge does:
      a mobile browser retracting its own toolbar as the keyboard opens is
      reported as the viewport growing, and the default freeze lets that
      through, so #root gets taller and everything anchored to its bottom is
      dragged down under the keyboard.

      The cost is that a genuine growth is ignored for as long as the hold
      lasts. Acceptable for a modal sheet — nothing behind it can be scrolled
      to retract a toolbar for real, and the pinned value is the resting
      height, so releasing it needs no recovery. Rotation is exempt via
      useAppViewport's own bypass. */
  freezeGrowth?: boolean;
}

/**
 * Opts a screen out of `--app-height` shrinking when the keyboard opens, so
 * its layout is exactly as tall with the keyboard up as it was without it.
 *
 * The default is the opposite, and deliberately so: chat's Composer has to
 * stay visible above the keyboard, which is only possible if the app shrinks
 * to the visible viewport (see useAppViewport). Onboarding wants the other
 * behaviour — the keyboard is a native overlay that covers whatever screen
 * region it occupies, and nothing already on screen may move to get out of
 * its way. A footer that keeps its y-position and ends up behind the keyboard
 * is the intended outcome, not a bug to reveal around.
 *
 * Freezing the height rather than sizing the screen from a viewport unit:
 * `dvh`/`svh`/`vh` were measured on this app's target WebKit to resolve
 * against a layout viewport ~20px off what is actually visible, which is why
 * useAppViewport measures instead. Reintroducing them for one screen would
 * mean two different, disagreeing ideas of how tall the app is. The measured
 * value simply stops being updated instead — the app keeps exactly the height
 * it had the frame before the keyboard appeared.
 *
 * Growth is not frozen by default, so the keyboard closing still restores the
 * height on the frame it is reported, and a genuine viewport change
 * (rotation) is committed by useAppViewport's own bypass. A caller that needs
 * growth held too passes `freezeGrowth`.
 */
export function useStaticAppHeight({ freezeGrowth = false }: StaticAppHeightOptions = {}): void {
  useEffect(() => {
    if (freezeGrowth) pins += 1;
    else holds += 1;
    return () => {
      if (freezeGrowth) pins -= 1;
      else holds -= 1;
      // Nudge useAppViewport to re-measure: every shrink during the freeze
      // was ignored, so if this unmounts while the keyboard is up (finishing
      // onboarding from the email step does exactly that) `--app-height` is
      // left taller than the visible viewport with no event of its own to
      // correct it.
      window.dispatchEvent(new Event("resize"));
    };
    // A caller that changes which hold it wants releases the old one and
    // takes the other; in practice every caller passes a constant.
  }, [freezeGrowth]);
}
